'use client';
import { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next-intl/link';
import { useLocaleRoutes } from '../utils/useLocaleRoutes';
import {
  KrsNr,
  NipNr,
  OrgNr,
  RegonNr,
  VatNr,
  YtunnusNr,
} from './ContactColumn';
import getOption from '../utils/getOption';
import { getPageData } from '../utils/getPageData';

const fixedPriceUrls = [
  'https://ATSNorway.no',
  'https://ATSSweden.se',
  'https://ATSPolska.pl',
  'https://ATSFinland.fi',
  'https://Landbrukssalg.no',
  'https://Lantbrukssalj.se',
];

const auctionUrls = ['https://ATSAuksjon.no', 'https://ATSAuktion.se'];

function getPageTitle(slug: string, locale: string) {
  const page = getPageData(slug) as any;
  const title =
    page['meta-title-' + locale] ||
    page['title-' + locale] ||
    page['name-' + locale] ||
    slug;
  return title;
}

export default function Footer() {
  const locale = useLocale();
  const t = useTranslations('navigation');
  const f = useTranslations('Footer');
  const n = useTranslations('Navbar');
  const path = useLocaleRoutes();
  const [isClient, setIsClient] = useState(false);

  return (
    <div className='bg-black'>
      <footer className='relative z-20 pb-4 pt-14 md:pt-28'>
        <div className='mx-auto max-w-[90rem] items-center px-4 md:px-10'>
          <div>
            <a href='/' className='mb-8'>
              <Image
                src='/images/ats-icon.svg'
                alt='ATS'
                width={0}
                height={0}
                priority
                style={{ width: '100px', height: 'auto' }}
              />
            </a>
          </div>
          <div className='mb-8 mt-4 flex flex-col justify-between gap-10 lg:flex-row'>
            <div>
              <p className='mb-12 max-w-[24em]'>
                {getOption('HAS_AUCTIONS') &&
                getOption('DEFAULT_LOCALE') === 'sv' ? (
                  <>
                    ATS Auktion är specialiserade på att förmedla försäljning
                    inom anläggnings- och transportbranschen på den globala
                    marknaden.{' '}
                    <span className='font-bold text-brand'>
                      Våga prova på - Det är helt kostnadsfritt om ditt objekt
                      inte säljs.
                    </span>{' '}
                    Det är vår försäljningsgaranti.
                  </>
                ) : (
                  f.rich('description', {
                    b: (chunks) => (
                      <b className='font-bold text-brand'>{chunks}</b>
                    ),
                  })
                )}
              </p>
              <h4 className='font-bold'>{f('contactHeader')}</h4>
              <div className='mt-2 flex max-w-[30em] flex-wrap justify-between gap-4 border-t border-accent pt-2 text-sm'>
                <div
                  className='mb-6'
                  dangerouslySetInnerHTML={{
                    __html: String(process.env.NEXT_PUBLIC_CONTACT).replaceAll(
                      '|',
                      '<br>'
                    ),
                  }}
                />
                <div className='flex flex-col'>
                  {!!OrgNr() ? (
                    <div>
                      <OrgNr />
                    </div>
                  ) : null}
                  {!!VatNr() ? (
                    <div>
                      <VatNr />
                    </div>
                  ) : null}
                  {!!KrsNr() ? (
                    <div>
                      <KrsNr />
                    </div>
                  ) : null}
                  {!!NipNr() ? (
                    <div>
                      <NipNr />
                    </div>
                  ) : null}
                  {!!RegonNr() ? (
                    <div>
                      <RegonNr />
                    </div>
                  ) : null}
                  {!!YtunnusNr() ? (
                    <div>
                      <YtunnusNr />
                    </div>
                  ) : null}
                  {process.env.NEXT_PUBLIC_MAIL && (
                    <div>
                      <Link
                        href={`mailto:${String(process.env.NEXT_PUBLIC_MAIL).replaceAll(' ', '')}`}
                        className='underline underline decoration-brand underline-offset-[.25em] transition duration-200 hover:text-brand'
                      >
                        {String(process.env.NEXT_PUBLIC_MAIL)}
                      </Link>
                    </div>
                  )}
                  {process.env.NEXT_PUBLIC_PHONE && (
                    <div>
                      <Link
                        href={`tel:${String(process.env.NEXT_PUBLIC_PHONE).replaceAll(' ', '')}`}
                        className='underline underline decoration-brand underline-offset-[.25em] transition duration-200 hover:text-brand'
                      >
                        {String(process.env.NEXT_PUBLIC_PHONE)}
                      </Link>
                    </div>
                  )}
                </div>
              </div>
              <div>
                {getOption('DEFAULT_LOCALE') === 'no' && (
                  <Image
                    src='/images/miljofyrtarn.svg'
                    alt='Miljøfyrtårn logo'
                    layout='intrinsic'
                    width={80}
                    height={0}
                    priority
                    style={{ width: '80px', height: 'auto' }}
                  />
                )}
              </div>
            </div>
            <div>
              <ul className='flex flex-col gap-y-2 border-l border-accent pl-8 lg:min-h-[20em]'>
                <li>
                  <Link
                    href={`/${path('contact')}`}
                    className='transition duration-200 hover:text-brand focus:text-brand'
                  >
                    {t('contact')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={`/${path('about')}`}
                    className='transition duration-200 hover:text-brand focus:text-brand'
                  >
                    {t('about')}
                  </Link>
                </li>
                {!!getOption('HAS_AUCTIONS') && (
                  <li>
                    <Link
                      href={`/${path('customer-center')}`}
                      className='transition duration-200 hover:text-brand focus:text-brand'
                    >
                      {n('customerCenter')}
                    </Link>
                  </li>
                )}
                {!!getOption('HAS_AUCTIONS') && (
                  <li>
                    <Link
                      href={`/${path('faq')}`}
                      className='transition duration-200 hover:text-brand focus:text-brand'
                    >
                      {t('faq')}
                    </Link>
                  </li>
                )}
                <li>
                  <Link
                    href={`/${path('why-ats')}`}
                    className='transition duration-200 hover:text-brand focus:text-brand'
                  >
                    {t('whyATS')}
                  </Link>
                </li>
                <li>
                  <Link
                    href={`/${path('privacy')}`}
                    className='transition duration-200 hover:text-brand focus:text-brand'
                  >
                    {getPageTitle('privacy', locale)}
                  </Link>
                </li>
                {(getOption('URL') === 'https://atsauksjon.no' ||
                  getOption('URL') === 'https://ats.no') && (
                  <li>
                    <a
                      href={`/files/redgjorelse_apenhetsloven.pdf`}
                      className='transition duration-200 hover:text-brand focus:text-brand'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {t('transparency-act')}
                    </a>
                  </li>
                )}
                <li>
                  <Link
                    href={`/${path('terms')}`}
                    className='transition duration-200 hover:text-brand focus:text-brand'
                  >
                    {getPageTitle('terms', locale)}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className='mb-4 text-2xl text-accent-light'>
                {f('atsLinkHeader')}
              </h3>
              <div className='flex flex-col gap-10 md:flex-row'>
                <div>
                  <Image
                    src='/images/fixed-price-icon.svg'
                    alt='ATS'
                    width={0}
                    height={0}
                    priority
                    style={{ width: '100px', height: 'auto' }}
                  />
                  <h4 className='my-4 uppercase text-brand'>
                    {f('fixedPriceHeader')}
                  </h4>
                  <ul
                    className='my-4 max-w-[20em] border-b border-accent lg:min-w-[14em]'
                    translate='no'
                  >
                    {fixedPriceUrls.map((url) => (
                      <li key={url}>
                        <Link
                          href={url.toLocaleLowerCase()}
                          target='_blank'
                          className='group flex items-center justify-between border-t border-accent py-1 transition duration-200 hover:text-brand focus:text-brand'
                        >
                          {url.replace('https://', '')}
                          <svg
                            width='15'
                            height='16'
                            viewBox='0 0 15 16'
                            xmlns='http://www.w3.org/2000/svg'
                            className='text-accent transition duration-200 group-hover:text-accent-light group-focus:text-accent-light'
                          >
                            <path
                              d='M5.875 3.50806H2.625C1.72754 3.50806 1 4.23559 1 5.13306V13.2581C1 14.1555 1.72754 14.8831 2.625 14.8831H10.75C11.6475 14.8831 12.375 14.1555 12.375 13.2581V10.0081M9.125 1.88306H14M14 1.88306V6.75806M14 1.88306L5.875 10.0081'
                              stroke='currentColor'
                              stroke-width='2'
                              stroke-linecap='round'
                              stroke-linejoin='round'
                            />
                          </svg>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <Image
                    src='/images/auction-icon.svg'
                    alt='ATS'
                    width={0}
                    height={0}
                    priority
                    style={{ width: '100px', height: 'auto' }}
                  />
                  <h4 className='my-4 uppercase text-brand'>
                    {f('auctionHeader')}
                  </h4>
                  <ul
                    className='my-4 max-w-[20em] border-b border-accent lg:min-w-[14em]'
                    translate='no'
                  >
                    {auctionUrls.map((url) => (
                      <li key={url}>
                        <Link
                          href={url.toLocaleLowerCase()}
                          target='_blank'
                          className='group flex items-center justify-between border-t border-accent py-1 transition duration-200 hover:text-brand focus:text-brand'
                        >
                          {url.replace('https://', '')}
                          <svg
                            width='15'
                            height='16'
                            viewBox='0 0 15 16'
                            xmlns='http://www.w3.org/2000/svg'
                            className='text-accent transition duration-200 group-hover:text-accent-light group-focus:text-accent-light'
                          >
                            <path
                              d='M5.875 3.50806H2.625C1.72754 3.50806 1 4.23559 1 5.13306V13.2581C1 14.1555 1.72754 14.8831 2.625 14.8831H10.75C11.6475 14.8831 12.375 14.1555 12.375 13.2581V10.0081M9.125 1.88306H14M14 1.88306V6.75806M14 1.88306L5.875 10.0081'
                              stroke='currentColor'
                              stroke-width='2'
                              stroke-linecap='round'
                              stroke-linejoin='round'
                            />
                          </svg>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <a
            href='https://atsgroup.com'
            target='_blank'
            className='group absolute bottom-0 right-0 flex h-[130px] w-[273px] flex-col items-center justify-center text-sm uppercase tracking-widest text-black'
          >
            <span className='scale-x-105 scale-y-95 transition duration-200 group-hover:text-accent group-focus:text-accent'>
              A part of
            </span>
            <div className='mt-1 border-t-2 border-black pt-2 transition duration-200 group-hover:border-accent group-hover:text-accent group-focus:border-accent group-focus:text-accent'>
              <svg
                width='124'
                height='22'
                viewBox='0 0 124 22'
                xmlns='http://www.w3.org/2000/svg'
                className='fill-current transition duration-200'
              >
                <path d='M48.7933 7.01486C48.646 7.01487 48.4988 7.11354 48.4988 7.31085L48.4996 19.939C48.4996 20.0869 48.4014 20.2349 48.205 20.2349L46.1431 20.2351C45.9958 20.2351 45.8485 20.1364 45.8485 19.9391L45.8477 7.31102C45.8476 7.16304 45.7495 7.01506 45.5531 7.01507L41.38 7.01534C41.2327 7.01535 41.0855 6.9167 41.0854 6.71939L41.0853 4.89423C41.0853 4.74625 41.1835 4.59826 41.3799 4.59824L52.868 4.5975C53.0153 4.59749 53.1626 4.69614 53.1626 4.89345L53.1627 6.76794C53.1627 6.91592 53.0645 7.06392 52.8682 7.06393L48.7933 7.01486Z' />
                <path d='M60.1341 6.6195C59.3486 6.61955 58.7595 6.76758 58.2686 7.11291C57.7776 7.40891 57.5322 7.90221 57.5322 8.54348C57.5323 9.18475 57.7778 9.67802 58.2688 9.97396C58.7597 10.2699 59.7907 10.6645 61.3618 11.059C62.9328 11.4535 64.1602 11.9961 64.9458 12.6866C65.7314 13.3771 66.1733 14.413 66.1734 15.7942C66.1734 17.1754 65.6826 18.2607 64.6516 19.0993C63.6207 19.938 62.2952 20.382 60.626 20.3821C58.2203 20.3823 56.0601 19.5438 54.1453 17.8668L55.618 16.0909C55.7162 15.9922 55.9126 15.9429 56.0108 16.0415C56.0108 16.0415 58.9566 17.9651 60.6749 17.965C61.5095 17.965 62.1968 17.7676 62.6878 17.4223C63.1787 17.077 63.4241 16.5837 63.4241 15.9917C63.424 15.3998 63.1785 14.9065 62.7367 14.5612C62.2948 14.216 61.4602 13.92 60.331 13.6241C59.2018 13.3282 58.3181 13.0817 57.7289 12.8844C57.1398 12.6378 56.5997 12.3418 56.1578 11.9966C55.225 11.306 54.7831 10.2208 54.783 8.7903C54.7829 7.35978 55.3228 6.22518 56.3538 5.43586C57.3847 4.64654 58.7102 4.25182 60.2322 4.25173C61.2141 4.25166 62.196 4.39959 63.1779 4.74482C64.1598 5.09006 64.9944 5.53396 65.7309 6.12586L64.5036 7.90176C64.4055 8.00042 64.2582 8.04976 64.1109 7.95112C64.1109 7.95112 63.2762 7.35923 62.4907 7.06331C61.6561 6.76739 60.8706 6.61945 60.1341 6.6195Z' />
                <path d='M42.2155 20.038L35.6849 4.94391C35.6358 4.79593 35.4394 4.69728 35.2922 4.69729L33.0338 4.69744C32.8865 4.69745 32.6902 4.79612 32.6411 4.94411L25.9652 19.9404C25.9161 20.0884 25.9652 20.1871 26.1125 20.187L28.3708 20.1869C28.5181 20.1869 28.7145 20.0882 28.7636 19.9402L30.138 16.9311C30.1871 16.7831 30.2362 16.6845 30.2362 16.6845C30.2362 16.6845 30.2853 16.5858 30.3344 16.4378L31.8561 12.9354C31.9052 12.7874 31.9542 12.6887 31.9542 12.6887C31.9542 12.6887 32.0033 12.5901 32.0524 12.4421L33.9668 8.10105C34.0159 7.95306 34.1141 7.95305 34.2123 8.10103L36.7165 13.9216C36.5692 13.9216 36.4219 13.9217 36.2256 13.971C33.2799 14.3658 31.6108 16.635 30.8745 17.523C30.1381 18.411 31.0709 17.9669 31.0709 17.9669C33.82 15.747 37.6495 16.536 37.6495 16.536C37.6986 16.536 37.7477 16.536 37.7967 16.536C37.8458 16.6346 37.8949 16.7333 37.8949 16.7333C37.8949 16.7333 37.944 16.8319 37.9932 16.9799L39.3189 20.0382C39.368 20.1862 39.5644 20.2848 39.7117 20.2848L41.97 20.2847C42.2155 20.2847 42.2646 20.186 42.2155 20.038Z' />
                <path d='M9.4694 20.0401C9.96034 19.8921 10.5986 19.7441 11.5804 19.6947C13.7406 19.7439 14.5262 20.3358 14.5262 20.3358C15.0171 20.6317 15.459 20.8783 15.8027 21.0263C16.1954 21.2729 16.6864 21.4209 17.1283 21.5195C17.521 21.6181 17.8647 21.6181 18.1101 21.6181C18.1592 21.6181 18.1592 21.6181 18.2083 21.6181C20.8594 21.5686 22.9213 19.398 22.872 16.7343C22.8227 14.0705 20.6624 11.9989 18.0113 11.999C17.9622 11.999 17.9622 11.999 17.9132 11.999C17.6186 11.9991 17.324 12.0484 16.9313 12.1471C16.4403 12.2458 15.9985 12.4431 15.6058 12.6898C15.2621 12.8871 14.8203 13.1338 14.3784 13.4298C14.3784 13.4298 13.593 14.0711 11.4328 14.1699C11.0401 14.1699 10.6964 14.1206 10.4018 14.1206C10.2055 14.8606 10.2055 15.2552 10.2055 15.2552L10.2055 15.3539C10.2546 15.8965 10.2547 16.3898 10.2547 16.8337C10.2547 17.3763 10.2057 17.919 10.0584 18.4616C9.96028 18.9549 9.76392 19.3002 9.61665 19.5468C9.61666 19.5962 9.61666 19.5962 9.56757 19.6455L9.51848 19.6948C9.51848 19.7442 9.46939 19.7935 9.46939 19.8428L9.4694 20.0401ZM15.5569 16.8827C15.5568 15.4522 16.6859 14.2682 18.1097 14.2188C19.5334 14.2187 20.7118 15.3532 20.7118 16.7837C20.7119 18.2143 19.5828 19.3982 18.1591 19.3983C16.7845 19.4477 15.6061 18.3132 15.5569 16.8827Z' />
                <path d='M6.52294 7.85618C6.42478 8.34947 6.22845 8.99075 5.78665 9.87869C4.70669 11.7039 3.77392 12.1479 3.77392 12.1479C3.283 12.4439 2.89026 12.6906 2.54661 12.8879C2.15387 13.1346 1.76113 13.4306 1.41749 13.7759C1.12294 14.0719 0.975672 14.3186 0.828407 14.6146C0.828407 14.6146 0.779316 14.6639 0.779319 14.7132C-0.49699 17.0317 0.337806 19.9421 2.64533 21.2245C4.95286 22.5068 7.84939 21.6681 9.1257 19.3496C9.12569 19.3002 9.12569 19.3002 9.17478 19.2509C9.32205 19.0042 9.46932 18.7083 9.56748 18.3136C9.71473 17.8203 9.7638 17.3764 9.76377 16.8831C9.76374 16.4885 9.76371 15.9952 9.76367 15.4032C9.76367 15.4032 9.61632 14.4167 10.5981 12.4928C10.7944 12.1475 10.9908 11.9008 11.1872 11.6048C10.6471 11.0623 10.3034 10.865 10.3034 10.865L10.2543 10.865C9.76336 10.6184 9.3215 10.3717 8.92873 10.1745C8.43776 9.92785 7.99589 9.58258 7.65221 9.2373C7.30852 8.89202 7.06303 8.54674 6.91573 8.34944C6.91573 8.30011 6.86663 8.30011 6.86663 8.30011L6.81753 8.25078C6.76844 8.20146 6.76843 8.15213 6.71934 8.10281L6.52294 7.85618ZM6.17972 14.7129C7.40713 15.4034 7.84908 16.9819 7.16184 18.2645C6.4746 19.547 4.9036 19.9911 3.6271 19.2513C2.35059 18.5114 1.90864 16.9823 2.64498 15.6997C3.38131 14.4171 4.95231 14.0223 6.17972 14.7129Z' />
                <path d='M18.6495 11.3084C18.3058 10.9631 17.8149 10.4699 17.2748 9.63131C16.2437 7.75689 16.3418 6.77031 16.3418 6.77031C16.3417 6.17837 16.3417 5.73442 16.2926 5.29046C16.2926 4.79718 16.1943 4.35323 16.047 3.85995C15.9488 3.46533 15.8015 3.16937 15.6542 2.92273C15.6542 2.87341 15.6051 2.87341 15.6051 2.82408C14.2303 0.555057 11.3337 -0.184684 9.07543 1.19666C6.81717 2.57801 6.08094 5.48844 7.45573 7.75746C7.45573 7.80679 7.50483 7.80678 7.50483 7.85611C7.65213 8.10275 7.84853 8.34938 8.14311 8.64533C8.4868 8.9906 8.87957 9.28655 9.27235 9.53317C9.61602 9.73046 10.0088 9.97708 10.5489 10.273C10.5489 10.273 11.4817 10.6676 12.6601 12.4433C12.8565 12.7886 13.0038 13.0846 13.102 13.3805C13.8384 13.1832 14.182 12.9858 14.182 12.9858C14.673 12.6898 15.0657 12.4432 15.4584 12.1965C15.9003 11.9005 16.4403 11.7032 16.9312 11.5551C17.4222 11.4071 17.8149 11.3578 18.0604 11.3578C18.1095 11.3578 18.1095 11.3578 18.1586 11.3577L18.2568 11.3577C18.3059 11.3577 18.355 11.3577 18.4041 11.3577L18.6495 11.3084ZM12.8561 7.5598C11.6288 8.2998 10.0578 7.90528 9.32125 6.67211C8.58476 5.43894 8.97741 3.86041 10.2047 3.1204C11.432 2.38039 13.0031 2.77492 13.7396 4.00809C14.4761 5.24125 14.0834 6.81979 12.8561 7.5598Z' />
                <path d='M79.0854 18.7531C78.6927 19.2957 78.1036 19.6904 77.4163 19.9371C76.6799 20.2331 75.8944 20.3318 74.9616 20.3319C74.0288 20.3319 73.1942 20.1347 72.4577 19.6908C71.7213 19.2469 71.1812 18.655 70.7884 17.8657C70.3956 17.0765 70.1991 16.1393 70.1991 15.054L70.199 14.1661C70.1989 12.4396 70.5916 11.1571 71.4261 10.1704C72.2607 9.23313 73.3898 8.73977 74.8135 8.73968C76.0409 8.7396 77.0719 9.03551 77.8084 9.67673C78.5448 10.318 78.9867 11.2058 79.1341 12.3404L77.1703 12.3405C76.9739 10.9593 76.1883 10.3181 74.8137 10.3182C73.9299 10.3183 73.2917 10.6143 72.8008 11.2556C72.359 11.8969 72.1136 12.8341 72.1137 14.018L72.1137 14.9059C72.1138 16.1391 72.3594 17.0764 72.8994 17.7176C73.3904 18.4082 74.1269 18.7041 75.0106 18.704C75.9925 18.704 76.7289 18.4573 77.1216 18.0133L77.1214 15.7935L74.814 15.7937L74.8139 14.3138L79.0851 14.3135L79.0854 18.7531Z' />
                <path d='M85.9585 15.8916L83.7983 15.8917L83.7986 20.2326L81.8348 20.2328L81.8341 8.93654L85.7617 8.93629C87.0381 8.93621 88.0691 9.23211 88.7565 9.82401C89.4438 10.4159 89.8366 11.2545 89.8367 12.3397C89.8368 13.0796 89.6404 13.7209 89.2968 14.2142C88.9532 14.7075 88.4132 15.1022 87.7749 15.3982L90.2791 20.0842V20.1829L88.168 20.183L85.9585 15.8916ZM83.7982 14.3132L85.8111 14.3131C86.4493 14.3131 86.9893 14.165 87.333 13.8197C87.7257 13.4744 87.873 13.0304 87.8729 12.4385C87.8729 11.8465 87.7256 11.3533 87.3819 11.0573C87.0382 10.712 86.5473 10.5641 85.8599 10.5641L83.798 10.5643L83.7982 14.3132Z' />
                <path d='M101.276 14.8547C101.276 15.9399 101.08 16.9265 100.687 17.7651C100.294 18.6037 99.7543 19.245 99.067 19.689C98.3797 20.133 97.5451 20.3797 96.6123 20.3798C95.6795 20.3799 94.894 20.1333 94.1575 19.6894C93.4211 19.2455 92.881 18.6042 92.4882 17.7657C92.0954 16.9271 91.8989 15.9899 91.8989 14.9046L91.8988 14.2634C91.8988 13.1781 92.0951 12.1916 92.4878 11.353C92.8805 10.5143 93.4205 9.87304 94.1078 9.42903C94.7951 8.98503 95.6296 8.73834 96.5624 8.73828C97.4952 8.73822 98.2808 8.93548 99.0172 9.37939C99.7046 9.8233 100.294 10.4645 100.637 11.3031C101.03 12.1417 101.227 13.0789 101.227 14.2134L101.227 14.8547L101.276 14.8547ZM99.3121 14.2629C99.312 13.0297 99.0665 12.0431 98.6246 11.4019C98.1336 10.7113 97.4954 10.4154 96.6116 10.4154C95.777 10.4155 95.0897 10.7608 94.6479 11.4021C94.157 12.0434 93.9116 12.9807 93.9117 14.2139L93.9117 14.8552C93.9118 16.0884 94.1573 17.075 94.6483 17.7162C95.1393 18.3574 95.7776 18.752 96.6613 18.752C97.545 18.7519 98.1832 18.4066 98.6741 17.7653C99.1159 17.124 99.3613 16.1374 99.3612 14.8548L99.3612 14.2629L99.3121 14.2629Z' />
                <path d='M112.027 8.93459L112.028 16.4819C112.028 17.6657 111.635 18.6523 110.899 19.343C110.113 20.0336 109.131 20.379 107.855 20.3791C106.578 20.3792 105.547 20.0339 104.811 19.3434C104.075 18.6528 103.682 17.7156 103.682 16.4824L103.681 8.93513L105.645 8.935L105.645 16.4823C105.645 17.2222 105.842 17.8141 106.235 18.2087C106.627 18.6033 107.168 18.8006 107.904 18.8006C109.426 18.8005 110.162 18.0112 110.162 16.4326L110.162 8.93471L112.027 8.93459Z' />
                <path d='M116.643 16.0376L116.643 20.2305L114.679 20.2307L114.678 8.93442L118.95 8.93415C120.226 8.93406 121.208 9.2793 121.944 9.92052C122.681 10.5617 123.074 11.4496 123.074 12.5349C123.074 13.6201 122.73 14.508 121.994 15.1C121.257 15.692 120.276 16.0374 118.95 16.0375L116.643 16.0376ZM116.642 14.4591L118.999 14.4589C119.686 14.4589 120.226 14.3109 120.57 13.9656C120.914 13.6202 121.11 13.1763 121.11 12.535C121.11 11.943 120.914 11.4498 120.57 11.0552C120.177 10.7099 119.686 10.5126 119.048 10.5127L116.642 10.5128L116.642 14.4591Z' />
              </svg>
            </div>
          </a>
          © {new Date().getFullYear()} {getOption('TITLE')}
        </div>
      </footer>
      <div className='relative z-10 -mt-[70px] flex h-[206px] items-end justify-end overflow-hidden text-black sm:-mt-[120px] md:-mt-[180px] lg:-mt-[206px]'>
        <div
          className='absolute right-0 top-0 h-[144px] w-[200%] transform bg-brand'
          style={{ transform: 'rotate(-26deg)', transformOrigin: 'top right' }}
        ></div>
      </div>
    </div>
  );
}
